import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
	clampToolsWidth,
	TOOLS_DEFAULT_WIDTH,
	TOOLS_MIN_WIDTH,
	toolsMaxWidth,
	toolsWidthStorageKey,
} from "./toolsWidth";

const useToolsMaxWidth = () => {
	const [max, setMax] = useState(() => toolsMaxWidth(window.innerWidth));

	useEffect(() => {
		const update = () => setMax(toolsMaxWidth(window.innerWidth));
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	return max;
};

const WIDTH_CHANGE_EVENT = "admin-tools-width-change";

const subscribeToStoredWidth = (onChange: () => void) => {
	window.addEventListener("storage", onChange);
	window.addEventListener(WIDTH_CHANGE_EVENT, onChange);
	return () => {
		window.removeEventListener("storage", onChange);
		window.removeEventListener(WIDTH_CHANGE_EVENT, onChange);
	};
};

const readStoredWidth = (key: string): number => {
	try {
		const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? "null");
		return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : TOOLS_DEFAULT_WIDTH;
	} catch {
		return TOOLS_DEFAULT_WIDTH;
	}
};

/**
 * The tools panel's width: 320px to half the viewport, 360px by default, saved per user in
 * `localStorage`. A saved width wider than the current limit is clamped for display but kept, so
 * returning to a bigger window restores it. A corrupt saved value reads as the default.
 */
export const useResizableToolsWidth = () => {
	const { user } = useAuthenticatedUser();
	const storageKey = toolsWidthStorageKey(user.id);
	const max = useToolsMaxWidth();
	const stored = useSyncExternalStore(subscribeToStoredWidth, () => readStoredWidth(storageKey));

	const [draft, setDraft] = useState<number | undefined>();

	const width = Math.min(Math.max(draft ?? stored, TOOLS_MIN_WIDTH), max);

	const setWidth = useCallback((next: number) => {
		setDraft(clampToolsWidth(Math.round(next), window.innerWidth));
	}, []);

	const commitWidth = useCallback(
		(next: number) => {
			const clamped = clampToolsWidth(Math.round(next), window.innerWidth);
			try {
				window.localStorage.setItem(storageKey, JSON.stringify(clamped));
			} catch {
				setDraft(undefined);
				return;
			}
			window.dispatchEvent(new Event(WIDTH_CHANGE_EVENT));
			setDraft(undefined);
		},
		[storageKey],
	);

	return {
		width,
		min: TOOLS_MIN_WIDTH,
		max,
		defaultWidth: TOOLS_DEFAULT_WIDTH,
		setWidth,
		commitWidth,
	};
};
